import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { daysLabel } from "../lib/format";
import {
  BookOpen, Headphones, Brain, Flame, Target, CalendarClock,
  Crown, ArrowRight, TrendingUp, Lock,
} from "lucide-react";

function Stat({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <Icon className="h-5 w-5 text-amber-600" />
      </div>
      <div className="mt-3 text-3xl font-heading font-extrabold text-[#0A192F]">{value}</div>
      <div className="text-sm text-slate-500">{label}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  const full = user?.has_full_access;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A192F]">My Learning</h1>
            <p className="text-slate-500 text-sm mt-1">Welcome back, {user?.name?.split(" ")[0]} — here's what to study next.</p>
          </div>
          {full ? (
            <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 border border-emerald-200 px-4 py-2 text-sm font-semibold text-emerald-700" data-testid="dashboard-access-badge">
              <Crown className="h-4 w-4" /> Full Access · {daysLabel(data?.access_days_remaining)}
            </div>
          ) : (
            <Link to="/upgrade">
              <Button className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="dashboard-upgrade-btn">
                <Crown className="h-4 w-4 mr-2" /> Upgrade to Full Access
              </Button>
            </Link>
          )}
        </div>

        {/* Free tier banner */}
        {!full && (
          <div className="rounded-xl bg-[#0A192F] text-white p-5 mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="free-tier-banner">
            <div className="flex items-center gap-3">
              <Lock className="h-5 w-5 text-amber-500" />
              <div>
                <div className="font-semibold">{Math.max(0, 10 - (data?.daily_used || 0))} of 10 practice questions remaining today</div>
                <div className="text-sm text-slate-300">Upgrade for the full workbook, audio library, and unlimited quizzes.</div>
              </div>
            </div>
            <Link to="/upgrade"><Button variant="secondary" className="bg-amber-600 hover:bg-amber-700 text-white border-0">Unlock $49.95</Button></Link>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat icon={Target} label="Exam Readiness" value={`${data?.readiness ?? 0}%`} testid="stat-readiness" />
          <Stat icon={Brain} label="Questions Answered" value={data?.questions_answered ?? 0} testid="stat-questions" />
          <Stat icon={Headphones} label="Course Progress" value={`${data?.audio_completed ?? 0}/${data?.total_lessons ?? 0}`} testid="stat-audio" />
          <Stat icon={CalendarClock} label="Exam Countdown" value={data?.exam_days_remaining != null ? `${data.exam_days_remaining}d` : "—"} sub={data?.exam_days_remaining == null ? "Set date in account" : "until your exam"} testid="stat-exam" />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Readiness + mastery */}
          <div className="lg:col-span-2 rounded-xl border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5 text-amber-600" /><h3 className="font-heading font-semibold text-lg text-[#0A192F]">Overall Readiness</h3></div>
            <div className="flex items-center gap-4">
              <Progress value={data?.readiness ?? 0} className="h-3" />
              <span className="font-heading font-bold text-[#0A192F]">{data?.readiness ?? 0}%</span>
            </div>
            <h4 className="font-heading font-semibold text-[#0A192F] mt-8 mb-3">Topic Mastery</h4>
            {data?.mastery?.length ? (
              <div className="space-y-3">
                {data.mastery.map((m) => (
                  <div key={m.topic}>
                    <div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{m.topic}</span><span className="font-mono text-slate-500">{m.score}%</span></div>
                    <Progress value={m.score} className="h-2" />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">Take a quiz to start tracking topic mastery.</p>
            )}
          </div>

          {/* Quick access */}
          <div className="space-y-4">
            <Link to="/quiz" className="block rounded-xl border border-slate-200 bg-white p-5 hover:shadow-lg hover:border-amber-300 transition group" data-testid="quick-quiz">
              <Brain className="h-6 w-6 text-amber-600 mb-2" />
              <div className="font-heading font-semibold text-[#0A192F] flex items-center justify-between">Practice Center <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" /></div>
              <p className="text-sm text-slate-500 mt-1">Knowledge checks with instant explanations</p>
            </Link>
            <Link to="/library" className="block rounded-xl border border-slate-200 bg-white p-5 hover:shadow-lg hover:border-amber-300 transition group" data-testid="quick-workbook">
              <BookOpen className="h-6 w-6 text-amber-600 mb-2" />
              <div className="font-heading font-semibold text-[#0A192F] flex items-center justify-between">My Library <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" /></div>
              <p className="text-sm text-slate-500 mt-1">Your courses &amp; what to study next</p>
            </Link>
            <Link to="/audio" className="block rounded-xl border border-slate-200 bg-white p-5 hover:shadow-lg hover:border-amber-300 transition group" data-testid="quick-audio">
              <Headphones className="h-6 w-6 text-amber-600 mb-2" />
              <div className="font-heading font-semibold text-[#0A192F] flex items-center justify-between">Audio Lessons <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition" /></div>
              <p className="text-sm text-slate-500 mt-1">Listen &amp; resume where you left off</p>
            </Link>
          </div>
        </div>
      </div>
    </Layout>
  );
}
